import LegalPage from "@/components/LegalPage";

const CommunityGuidelinesPage = () => (
  <LegalPage
    title="Review & Community Guidelines"
    updated="June 18, 2026"
    intro={
      <p>
        Vecurate is most useful when reviews are honest, specific, and constructive. These guidelines
        explain how to write reviews that genuinely help future students and trainees — and what to
        avoid. They sit alongside our <a href="/content-policy">Content Policy</a> and{" "}
        <a href="/terms">Terms of Service</a>.
      </p>
    }
  >
    <h2>1. What Makes a Great Review</h2>
    <ul>
      <li><strong>Firsthand experience.</strong> Write only about rotations, internships, programs, or supervisors you have personally experienced.</li>
      <li><strong>Specific and concrete.</strong> Describe what you did, what you learned, the workload, the teaching style, and the day-to-day environment.</li>
      <li><strong>Balanced.</strong> Strong reviews acknowledge both strengths and weaknesses. Few experiences are entirely good or entirely bad.</li>
      <li><strong>Constructive.</strong> Frame feedback in a way that could actually help the program or supervisor improve — and help readers make a decision.</li>
      <li><strong>Respectful.</strong> Disagreement, criticism, and even strong negative opinions are welcome when expressed without personal attacks.</li>
    </ul>

    <h2>2. What to Avoid</h2>
    <ul>
      <li><strong>Defamation.</strong> Do not make false statements of fact that could damage a person's or organization's reputation. Stick to your own experiences and to opinions clearly framed as opinions.</li>
      <li><strong>Personal attacks.</strong> Do not insult, demean, or use slurs against any individual.</li>
      <li><strong>Naming private individuals harmfully.</strong> Avoid naming patients, classmates, junior staff, or other private individuals in a way that could harm them. Supervisors and preceptors acting in their professional capacity may be named factually and proportionately.</li>
      <li><strong>Identifying details about third parties.</strong> Do not include details that could identify patients (PHI), coworkers, classmates, or other non-public individuals.</li>
      <li><strong>Off-topic content.</strong> Reviews are about the educational or work experience — not about unrelated political or personal grievances.</li>
      <li><strong>Vote manipulation or duplicate posts.</strong> Do not post the same review multiple times, ask others to post on your behalf, or coordinate to push ratings up or down.</li>
    </ul>

    <h2>3. Focus on Experiences, Not People</h2>
    <p>
      A review's job is to describe the <em>experience</em> — the structure of the rotation, the
      learning opportunities, the workload, the support, the culture. When discussing a supervisor or
      preceptor, focus on their professional conduct (teaching, feedback, supervision, accessibility)
      rather than personal characteristics.
    </p>

    <h2>4. Examples</h2>

    <h3>Helpful (acceptable)</h3>
    <blockquote>
      "This rotation was busy but well-organized. Attendings ran short teaching sessions twice a week
      and were generous with feedback. Expect long hours during the first two weeks. Documentation
      expectations were unclear at the start — bringing your own template helps."
    </blockquote>
    <blockquote>
      "My preceptor was direct and held high expectations. Feedback was specific and actionable, though
      it could feel blunt. I learned a lot, but this placement may be challenging if you prefer a
      gentler teaching style."
    </blockquote>
    <blockquote>
      "Solid summer internship. Real responsibility, structured weekly check-ins, and clear deliverables.
      Salary was on the low end for the area. Limited opportunities to rotate across teams."
    </blockquote>

    <h3>Not helpful (will be removed)</h3>
    <blockquote>
      "Dr. [Name] is a terrible human being and everyone should avoid them." — Personal attack with no
      experience described.
    </blockquote>
    <blockquote>
      "Worked with a patient named John S. with [diagnosis]…" — Contains protected health information.
    </blockquote>
    <blockquote>
      "I never actually went here but I heard it's awful." — Not a firsthand experience.
    </blockquote>
    <blockquote>
      "Everyone in our cohort: post 1-star reviews until they apologize." — Coordinated reputation
      attack and vote manipulation.
    </blockquote>

    <h2>5. Editing and Updating Your Review</h2>
    <p>
      Experiences and programs change over time. If your view of a rotation, internship, or supervisor
      evolves after additional context, consider submitting an updated review describing the change
      rather than escalating the tone of an existing one.
    </p>

    <h2>6. Reporting Violations</h2>
    <p>
      If you see a review that breaks these guidelines, report it from the review itself or email{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>. We review reports and take action
      consistent with our <a href="/content-policy">Content Policy</a>.
    </p>

    <div className="callout">
      <strong>The short version:</strong> share what you actually experienced, be specific, be fair, and
      protect the privacy of people who haven't chosen to be on Vecurate.
    </div>
  </LegalPage>
);

export default CommunityGuidelinesPage;
